import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash.isequal';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';

const useParcels = values => {
  const [currentParcel, setCurrentParcel] = useState(null);
  const [isCreatingParcel, setIsCreatingParcel] = useState(false);
  const [isDeletingParcel, setIsDeletingParcel] = useState(false);
  const [parcels, setParcels] = useState(values);
  const [parcelQuantities, setParcelQuantities] = useState({});
  const [showParcelsDialog, setShowParcelsDialog] = useState(false);
  const valuesRef = useRef(values);
  const valuesIsDifferent = !isEqual(values, valuesRef.current);

  useEffect(() => {
    valuesRef.current = values;
  });

  useEffect(() => {
    if (valuesIsDifferent) {
      const [lastParcel] = (valuesRef.current ?? []).slice(-1);

      if (!currentParcel && lastParcel) {
        setCurrentParcel(lastParcel);
      }

      setParcels(valuesRef.current ?? []);
    }
  }, [valuesIsDifferent, currentParcel]);

  const createParcel = useCallback(async (data, options = {}) => {
    try {
      setIsCreatingParcel(true);

      const response = await parcelsApi.createParcel(data);
      const { data: newParcel } = response;
      const { setAsCurrent = true } = options;

      setParcels(oldParcels => [...oldParcels, newParcel]);

      if (setAsCurrent) {
        setCurrentParcel(newParcel);
      }

      return newParcel;
    } catch (err) {
      return Promise.reject(err);
    } finally {
      setIsCreatingParcel(false);
    }
  }, []);

  const deleteParcel = useCallback(
    async parcelId => {
      try {
        setIsDeletingParcel(true);

        await parcelsApi.removeParcel(parcelId);

        const updatedParcels = parcels.filter(parcel => parcel.id !== parcelId);
        setParcels(updatedParcels);

        if (currentParcel?.id === parcelId) {
          const [lastParcel] = updatedParcels.slice(-1);
          setCurrentParcel(lastParcel ?? null);
        }

        return true;
      } catch (err) {
        return Promise.reject(err);
      } finally {
        setIsDeletingParcel(false);
      }
    },
    [currentParcel, parcels],
  );

  return {
    createParcel,
    currentParcel,
    deleteParcel,
    isCreatingParcel,
    isDeletingParcel,
    parcelQuantities,
    parcels,
    setCurrentParcel,
    setShowParcelsDialog,
    setParcelQuantities,
    setParcels,
    showParcelsDialog,
  };
};

export default useParcels;
